/**
 * Chat URL State Management Hook
 *
 * Provides centralized URL state management for chat history, enabling
 * bookmarkable and shareable chat URLs via path parameters.
 *
 * @module useChatURLState
 * @see src/pages/library/hooks/useLibrarySearch.ts - Reference implementation pattern
 */

import { useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";

import { isValidChatId } from "@/utils/chatIdValidation";
import { logger } from "@/telemetry";

/**
 * Return type for the useChatURLState hook
 */
interface UseChatURLStateReturn {
  /**
   * Currently active chat ID from URL, or null if no valid chat ID present
   * This is validated against UUID v4 format
   */
  chatId: string | null;

  /**
   * Current workflow ID from URL, or undefined if on default chat route
   */
  workflowId: string | undefined;

  /**
   * Navigate to a specific chat, updating the URL path parameter
   *
   * @param chatId - UUID v4 chat ID to navigate to
   * @param workflowId - Optional workflow ID (defaults to current or 'chat_agent')
   *
   * @example
   * ```typescript
   * // Navigate to chat in default workflow
   * navigateToChat("550e8400-e29b-41d4-a716-446655440000")
   * // URL: /chat/550e8400-e29b-41d4-a716-446655440000
   *
   * // Navigate to chat in specific workflow
   * navigateToChat("550e8400-e29b-41d4-a716-446655440000", "research_agent")
   * // URL: /workflows/research_agent/550e8400-e29b-41d4-a716-446655440000
   * ```
   */
  navigateToChat: (chatId: string, workflowId?: string) => void;

  /**
   * Clear the chat ID from URL, returning to the base route
   * Uses replace: true to avoid cluttering browser history
   *
   * @example
   * ```typescript
   * clearChatId()
   * // From: /chat/550e8400... → To: /chat
   * // From: /workflows/research_agent/550e8400... → To: /workflows/research_agent
   * ```
   */
  clearChatId: () => void;

  /**
   * Get the current chat ID from URL without memoization side effects
   * Useful for one-time initialization checks
   *
   * @returns Validated chat ID or null
   */
  getChatIdFromURL: () => string | null;
}

/**
 * Custom hook for managing chat URL state via path parameters
 *
 * This hook provides a centralized interface for reading and updating chat IDs
 * in the URL, following the path parameter pattern established in Phase 1:
 * - `/chat/:chatId` - Default chat workflow
 * - `/workflows/:workflowId/:chatId` - Specific workflow chat
 *
 * Key features:
 * - Automatic UUID v4 validation of chat IDs
 * - Type-safe path parameter handling
 * - Browser history management (push vs replace)
 * - Support for both default and workflow-specific chats
 *
 * @returns Object with chatId, workflowId, and navigation functions
 *
 * @example
 * ```typescript
 * function ChatComponent() {
 *   const { chatId, navigateToChat, clearChatId } = useChatURLState();
 *
 *   // Check if chat ID is in URL
 *   if (chatId) {
 *     loadChat(chatId);
 *   }
 *
 *   // Navigate to a chat
 *   const handleChatSelect = (id: string) => {
 *     navigateToChat(id);
 *   };
 *
 *   // Clear invalid chat
 *   const handleError = () => {
 *     clearChatId();
 *   };
 * }
 * ```
 *
 * @security
 * - All chat IDs are validated via isValidChatId() before navigation
 * - Invalid chat IDs are treated as null (fail-safe)
 * - Prevents XSS via URL parameter validation
 */
export const useChatURLState = (): UseChatURLStateReturn => {
  // Extract path parameters from current route
  const { chatId: chatIdParam, workflowId } = useParams<{
    chatId?: string;
    workflowId?: string;
  }>();
  const navigate = useNavigate();

  // Validate and memoize chat ID from URL
  // Returns null if chatIdParam is invalid or missing
  const chatId = useMemo(() => {
    return isValidChatId(chatIdParam) ? chatIdParam : null;
  }, [chatIdParam]);

  /**
   * Navigate to a specific chat by ID
   *
   * Validation:
   * - Validates chat ID format before navigation
   * - Logs error and exits early if invalid
   *
   * Route Selection:
   * - Uses 'chat_agent' as default workflow if none specified
   * - Routes to /chat for default workflow
   * - Routes to /workflows/:workflowId for specific workflows
   *
   * History Management:
   * - Uses replace: false (push) to allow browser back button
   */
  const navigateToChat = useCallback(
    (chatId: string, workflow?: string) => {
      // Security: Validate chat ID format before navigation
      if (!isValidChatId(chatId)) {
        logger.error("Invalid chat ID format", undefined, { chatId });

        return;
      }

      // Determine target workflow (provided > current > default)
      const targetWorkflowId = workflow || workflowId || "chat_agent";

      // Build base path based on workflow
      const basePath =
        targetWorkflowId === "chat_agent" ? "/chat" : `/workflows/${targetWorkflowId}`;

      // Navigate with push (not replace) to enable back button
      navigate(`${basePath}/${chatId}`, { replace: false });
    },
    [navigate, workflowId],
  );

  /**
   * Clear chat ID from URL, returning to base route
   *
   * Route Selection:
   * - Returns to /chat for default workflow
   * - Returns to /workflows/:workflowId for specific workflows
   *
   * History Management:
   * - Uses replace: true to avoid adding empty state to history
   */
  const clearChatId = useCallback(() => {
    // Determine base path based on current workflow
    const basePath = workflowId ? `/workflows/${workflowId}` : "/chat";

    // Navigate with replace to avoid history clutter
    navigate(basePath, { replace: true });
  }, [navigate, workflowId]);

  /**
   * Get chat ID from URL without memoization
   * Useful for one-time initialization where reactivity isn't needed
   */
  const getChatIdFromURL = useCallback(() => {
    return isValidChatId(chatIdParam) ? chatIdParam : null;
  }, [chatIdParam]);

  return {
    chatId,
    workflowId,
    navigateToChat,
    clearChatId,
    getChatIdFromURL,
  };
};
